import {
    ChangeDetectionStrategy,
    Component,
    inject,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatFormFieldModule, MatSuffix } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { AuthService } from '../../../../core/auth/auth.service';
import { UserService } from '../../../../core/user/user.service';

@Component({
    selector: 'change-password-dialog',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        TranslocoPipe,
        MatSuffix,
        FuseAlertComponent,
    ],
    templateUrl: './change-password-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordDialogComponent {
    private dialogRef = inject(MatDialogRef);
    private authService = inject(AuthService);
    private userService = inject(UserService);
    private translocoService = inject(TranslocoService);
    private fb = inject(FormBuilder);

    form: FormGroup = this.fb.group({
        oldPassword: ['', Validators.required],
        password: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', Validators.required],
    });

    alert: { type: FuseAlertType; message: string } | null = null;
    submitting = false;
    showOldPassword = false;
    showNewPassword = false;
    showConfirmPassword = false;

    submit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const { oldPassword, password, confirmPassword } = this.form.value;

        if (password !== confirmPassword) {
            this.alert = {
                type: 'error',
                message: this.translocoService.translate('auth.change_password.mismatch'),
            };
            return;
        }

        this.submitting = true;
        this.alert = null;

        this.authService.changePassword(oldPassword, password).subscribe({
            next: () => {
                this.userService.forcePasswordChange = false;
                this.dialogRef.close(true);
            },
            error: () => {
                this.submitting = false;
                this.alert = {
                    type: 'error',
                    message: this.translocoService.translate('auth.change_password.error'),
                };
            },
        });
    }

    close(): void {
        this.dialogRef.close();
    }
}
